import Controller from '@ember/controller';
import { cached, tracked } from '@glimmer/tracking';
import type EventModel from 'donor-elf-web/models/event';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import {
  deserializeAppliedFilters,
  serializeAppliedFilters,
  type AppliedPropertyFilter,
  type PropertyFilterInfoForQueryFilter,
} from 'donor-elf-web/lib/filter-utils-new';
import { decamelizeKeys } from 'donor-elf-web/lib/object-utils';
import { isEmpty } from '@ember/utils';
import { eventTypeNames } from 'donor-elf-web/lib/event-type-names';
const PAGE_SIZE = 50;

export default class NewsFeedController extends Controller {
  @service store!: Store;

  queryParams = ['page', 'filters'];

  @tracked filters?: string;
  @tracked page = 1;
  @tracked totalPages = 0;
  @tracked loading = true;
  @tracked error?: any;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked events: EventModel[] = [];

  templateDidInsert = () => {
    this.deserializeFilters();
    this.load();
  };

  templateWillDestroy = () => {
    this.loading = true;
    this.error = undefined;
    this.events = [];
    this.page = 1;
    this.totalPages = 0;
    this.filters = undefined;
    this.appliedFilters = undefined;
  };

  async load() {
    this.loading = true;
    this.error = undefined;

    try {
      let queryFilter: any = {
        offset: (this.page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      let { availablePropertyFilters } = this;
      for (let appliedFilter of this.appliedFilters || []) {
        let propertyFilter = availablePropertyFilters.find(
          (x) => x.propertyName === appliedFilter.propertyName,
        )!;
        await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
      }
      const response = await this.store.query('event', {
        filter: decamelizeKeys(queryFilter),
      });
      let totalCount = (response as any).meta?.total_count as number;
      if (isEmpty(totalCount)) {
        totalCount = 0;
      }
      this.totalPages = Math.ceil(totalCount / PAGE_SIZE);
      this.events = response.slice();
    } catch (error) {
      this.error = error;
    }
    this.loading = false;
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let list: PropertyFilterInfoForQueryFilter[] = [];
    list.push({
      propertyName: 'from',
      label: 'Date',
      propertyType: 'date',
      availableOperations: ['>=', '<=', '><', '='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '>=') {
          queryFilter.from = appliedFilter.val;
        } else if (appliedFilter.operation === '<=') {
          queryFilter.to = appliedFilter.val;
        } else if (appliedFilter.operation === '><') {
          let dates = appliedFilter.val as string[];
          queryFilter.from = dates[0];
          queryFilter.to = dates[1];
        } else if (appliedFilter.operation === '=') {
          queryFilter.from = appliedFilter.val;
          queryFilter.to = appliedFilter.val;
        }
      },
    });
    list.push({
      propertyName: 'eventTypeId',
      label: 'Event Type',
      propertyType: 'list',
      availableOperations: ['any'],
      listOptions: Object.keys(eventTypeNames).map((eventTypeId: any) => {
        return { val: eventTypeId, label: eventTypeNames[eventTypeId]! };
      }),
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.eventTypeId = appliedFilter.val;
      },
    });
    return list;
  }

  handleFiltersChanged = (currentFilters: AppliedPropertyFilter[]) => {
    this.page = 1;
    this.totalPages = 0;
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  };

  handleChangePage = (page: number) => {
    this.page = page;
    this.serializeFilters();
    this.load();
  };

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }
}
